import React, { useEffect, useState, useCallback } from 'react';
import { Icon, Button, ContentBlock, Tag, Input } from '../../../pages/DesignSystem';
import {
  SUBSCRIPTION_PLANS,
  SubscriptionPlan,
  SubscriptionPlanId,
  getPlanById,
  formatPrice,
  formatPriceWithInterval,
  getLaunchPriceInfo,
  isPro
} from '../../../types/subscription.types';
import { useSubscription } from '../../../stores/subscriptionStore';
import { useOrganizer } from '../../../contexts/OrganizerContext';
import stripeService from '../../../supabase/stripe.service';
import { supabase } from '../../../config/supabase';
import './SubscriptionModal.css';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizerId: string;
}

/**
 * SubscriptionModal - Desktop modal for managing subscription
 * Shows current plan and upgrade options
 */
const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  organizerId
}) => {
  const { subscription } = useSubscription();
  const { organizerProfile } = useOrganizer();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Referral system states
  const [referrerEmail, setReferrerEmail] = useState(organizerProfile?.referrerEmail || '');
  const [referrerValid, setReferrerValid] = useState<boolean | null>(null);
  const [referrerName, setReferrerName] = useState('');
  const [checkingReferrer, setCheckingReferrer] = useState(false);
  const timeoutIdRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debounced function to check referrer validity via secure RPC
  const checkReferrer = useCallback(
    (email: string) => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      if (!email || !email.includes('@')) {
        setReferrerValid(null);
        setReferrerName('');
        setCheckingReferrer(false);
        return;
      }

      setCheckingReferrer(true);

      timeoutIdRef.current = setTimeout(async () => {
        try {
          // Use secure RPC to validate referrer without exposing sensitive data
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data, error } = await (supabase.rpc as any)('validate_referrer_code', {
            referrer_email: email
          });

          if (error) {
            console.error('Error checking referrer:', error);
            setReferrerValid(false);
            setReferrerName('Error validating referrer');
            return;
          }

          const result = Array.isArray(data) ? data[0] : data;

          if (!result) {
            setReferrerValid(false);
            setReferrerName('Error validating referrer');
          } else if (result.is_valid) {
            setReferrerValid(true);
            setReferrerName(result.organizer_name || email);
          } else {
            setReferrerValid(false);
            // Map error reasons to user-friendly messages
            switch (result.error_reason) {
              case 'not_found':
                setReferrerName('User not found');
                break;
              case 'self_referral':
                setReferrerName('You cannot refer yourself');
                break;
              case 'not_paid_subscriber':
                setReferrerName('This user must have an active paid subscription');
                break;
              default:
                setReferrerName('Invalid referrer email');
            }
          }
        } catch (err) {
          console.error('Error checking referrer:', err);
          setReferrerValid(false);
          setReferrerName('Error validating referrer');
        } finally {
          setCheckingReferrer(false);
        }
      }, 500);
    },
    []
  );

  // Handle referrer email change
  const handleReferrerEmailChange = (value: string) => {
    setReferrerEmail(value);
    checkReferrer(value);
  };

  // Handle escape key and body scroll lock
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle plan selection - redirect to Stripe Checkout
  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (isLoading) return;

    // Validate referrer if email is provided
    if (referrerEmail && referrerValid !== true) {
      setError('Please enter a valid referrer email or leave it empty');
      return;
    }

    setError(null);
    setIsLoading(true);
    setLoadingPlanId(plan.id);

    try {
      // Pass referrerEmail only for subscriptions (monthly/yearly), not for pay-per-event
      const referrerEmailParam = (plan.id !== 'pay_per_event' && referrerEmail && referrerValid)
        ? referrerEmail.trim()
        : undefined;

      await stripeService.redirectToCheckout(
        plan.id as SubscriptionPlanId,
        organizerId,
        undefined,
        referrerEmailParam
      );
    } catch (err) {
      console.error('Checkout error:', err);
      setError((err as Error).message || 'Failed to start checkout');
      setIsLoading(false);
      setLoadingPlanId(null);
    }
  };

  // Handle portal redirect for subscription management
  const handleManageSubscription = async () => {
    if (isLoading) return;

    setError(null);
    setIsLoading(true);

    try {
      await stripeService.redirectToPortal(organizerId);
    } catch (err) {
      console.error('Portal error:', err);
      setError((err as Error).message || 'Failed to open billing portal');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentPlan = subscription ? getPlanById(subscription.planId) : getPlanById('free');
  const isProPlan = subscription ? isPro(subscription.planId) : false;

  // Get upgrade plans (exclude current and pay_per_event for pro users)
  const upgradePlans = SUBSCRIPTION_PLANS.filter(plan => {
    if (plan.id === 'free') return false;
    if (plan.id === subscription?.planId) return false;
    if (isProPlan && plan.id === 'pay_per_event') return false;
    return true;
  });

  // Format date
  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="subscription-modal__backdrop" onClick={handleBackdropClick}>
      <div className="subscription-modal__container">
        {/* Modal Header */}
        <header className="subscription-modal__header">
          <h2 className="subscription-modal__title">
            {isProPlan ? 'Manage Subscription' : 'Choose Your Plan'}
          </h2>
          <button
            className="subscription-modal__close"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <Icon name="X" size={24} />
          </button>
        </header>

        {/* Modal Content */}
        <div className="subscription-modal__content">
          {/* Current Plan Section */}
          <ContentBlock className="subscription-modal__current-plan" border="light">
            <div className="subscription-modal__section-header">
              <Icon name="Crown" size={20} />
              <h3>Current Plan</h3>
            </div>

            <div className="subscription-modal__plan-info">
              <div className="subscription-modal__plan-badge-wrapper">
                <div className="subscription-modal__plan-badge">
                  <Tag
                    label={currentPlan.name}
                    type="status"
                    status={isProPlan ? 'published' : 'draft'}
                    size="md"
                  />
                  <span className="subscription-modal__plan-price">
                    {formatPriceWithInterval(currentPlan)}
                  </span>
                </div>
                {subscription && subscription.planId === 'yearly' && (
                  <div className="subscription-modal__savings-badge">
                    4 months free
                  </div>
                )}
              </div>

              {/* Plan Details */}
              <div className="subscription-modal__plan-details">
                {isProPlan ? (
                  <>
                    <div className="subscription-modal__detail-row">
                      <Icon name="Check" size={16} />
                      <span>Unlimited events</span>
                    </div>
                    {subscription && (
                      <div className="subscription-modal__detail-row">
                        <Icon name="Calendar" size={16} />
                        <span>
                          {subscription.cancelAtPeriodEnd
                            ? `Expires: ${formatDate(subscription.currentPeriodEnd)}`
                            : `Renews: ${formatDate(subscription.currentPeriodEnd)}`}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="subscription-modal__detail-row">
                      <Icon name="Calendar" size={16} />
                      <span>
                        {subscription?.eventsUsed ?? 0}/{subscription?.eventsLimit ?? 5} events used
                      </span>
                    </div>
                    <div className="subscription-modal__usage-bar">
                      <div
                        className="subscription-modal__usage-fill"
                        style={{
                          width: `${Math.min(100, ((subscription?.eventsUsed ?? 0) / (subscription?.eventsLimit ?? 5)) * 100)}%`
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </ContentBlock>

          {/* Error message */}
          {error && (
            <div className="subscription-modal__error">
              <Icon name="AlertCircle" size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* FREE USER: Show upgrade plans */}
          {!isProPlan && (
            <div className="subscription-modal__upgrade-section">
              <h3 className="subscription-modal__upgrade-title">Upgrade Plans</h3>
              <p className="subscription-modal__upgrade-subtitle">
                Choose the plan that works best for you
              </p>
              <p className="subscription-modal__value-proposition">
                Events typically cost $10–$20 per person. Just 1–2 clients brought through the app covers your subscription — 3 or more and you're making profit!
              </p>

              <div className="subscription-modal__plans-grid">
                {upgradePlans.map((plan) => {
                  const isRecommended = plan.id === 'yearly';
                  const isPayPerEvent = plan.id === 'pay_per_event';
                  const launchInfo = getLaunchPriceInfo(plan);

                  return (
                    <div
                      key={plan.id}
                      className={`subscription-modal__plan-card ${
                        isRecommended ? 'subscription-modal__plan-card--recommended' : ''
                      }`}
                    >
                      {isRecommended && (
                        <div className="subscription-modal__recommended-badge">Best Value</div>
                      )}

                      <div className="subscription-modal__plan-card-header">
                        <h4>{plan.name}</h4>
                        {launchInfo.isLaunchPrice && (
                          <Tag label="Launch Price" type="status" status="published" size="sm" />
                        )}
                        {launchInfo.isLaunchPrice && (
                          <span className="subscription-modal__regular-price">
                            {launchInfo.regularPriceFormatted}
                          </span>
                        )}
                        <p className="subscription-modal__plan-card-price">
                          <span className="subscription-modal__price-value">
                            ${formatPrice(plan.price)}
                          </span>
                          {plan.interval && plan.interval !== 'one_time' && (
                            <span className="subscription-modal__price-interval">
                              /{plan.interval}
                            </span>
                          )}
                          {plan.interval === 'one_time' && (
                            <span className="subscription-modal__price-interval">
                              /event
                            </span>
                          )}
                        </p>
                      </div>

                      {plan.id === 'yearly' && (
                        <div className="subscription-modal__savings-badge">4 months free</div>
                      )}

                      <ul className="subscription-modal__plan-card-features">
                        {plan.features.map((feature, index) => (
                          <li key={index}>
                            <Icon name="Check" size={16} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {launchInfo.isLaunchPrice && (
                        <p className="subscription-modal__launch-hint">
                          Lock in this rate before we raise prices
                        </p>
                      )}

                      <Button
                        variant={isRecommended ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => handleSelectPlan(plan)}
                        disabled={isLoading}
                        className="subscription-modal__plan-card-button"
                      >
                        {loadingPlanId === plan.id
                          ? 'Loading...'
                          : isPayPerEvent
                          ? 'Buy Event'
                          : 'Select Plan'}
                      </Button>
                    </div>
                  );
                })}
              </div>

              {/* Referral Section - Below plans */}
              <div className="subscription-modal__referral-section">
                <Input
                  id="referrer-email-modal"
                  name="referrerEmail"
                  label="Referred by (optional)"
                  type="email"
                  value={referrerEmail}
                  onChange={handleReferrerEmailChange}
                  placeholder="friend@example.com"
                  icon="Users"
                  helper="You get 1 month free. They get 1 month free when you become a paying customer!"
                  disabled={isLoading}
                />

                {/* Checking state */}
                {checkingReferrer && (
                  <div className="subscription-modal__referral-status subscription-modal__referral-status--checking">
                    <Icon name="Loader" size={14} />
                    <span>Checking...</span>
                  </div>
                )}

                {/* Success state */}
                {referrerValid === true && !checkingReferrer && (
                  <div className="subscription-modal__referral-status subscription-modal__referral-status--success">
                    <Icon name="Check" size={16} />
                    <span>You'll get 1 month free thanks to {referrerName} !</span>
                  </div>
                )}

                {/* Error state */}
                {referrerValid === false && referrerEmail && !checkingReferrer && (
                  <div className="subscription-modal__referral-status subscription-modal__referral-status--error">
                    <Icon name="AlertCircle" size={16} />
                    <span>{referrerName || 'Invalid referrer email'}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PRO USER: Show management actions */}
          {isProPlan && subscription && (
            <div className="subscription-modal__management-section">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleManageSubscription}
                disabled={isLoading}
                className="subscription-modal__change-plan-button"
              >
                {isLoading ? 'Loading...' : 'Change plan'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleManageSubscription}
                disabled={isLoading}
                className="subscription-modal__cancel-button"
              >
                {isLoading ? 'Loading...' : 'Cancel subscription'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
